import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkle, X, ChevronRight, ChevronLeft, MessageCircle, User,
  Sparkles, Bell, CalendarDays, CreditCard, Mic, Gift, Volume2, VolumeX, Square,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useVoice } from "@/contexts/VoiceAssistantContext";

const TUTORIAL_KEY = "ca_tutorial_done_v2";

const STEPS = [
  {
    icon: Sparkle,
    color: "from-purple-600 to-fuchsia-700",
    title: "¡Bienvenido a CrisAngel Astral! 🌙",
    desc: "Te guiaré rápidamente por todo lo que puedes hacer aquí. Si activaste la voz, te lo iré leyendo paso a paso; puedes detenerme con el botón rojo o saltar este tutorial cuando quieras.",
  },
  {
    icon: Volume2,
    color: "from-[#d4af37] to-amber-500",
    title: "🔊 Tu asistente de voz",
    desc: "A la izquierda de la pantalla está el asistente de voz. Te lee cada paso, te avisa cuando Cristopher aprueba tu pago o te responde, y con el micrófono puedes preguntarle o dictar tus mensajes. En el engrane eliges voz femenina o masculina y la velocidad.",
  },
  {
    icon: MessageCircle,
    color: "from-amber-500 to-yellow-600",
    title: "💬 Chat con Cristopher, 24 horas",
    desc: "El chat dorado, abajo a la derecha, te responde al instante a cualquier hora. Ahí mismo puedes registrar tu pago y agendar tu cita con mini-aplicaciones dentro del chat, sin salir de la conversación. Cristopher revisa todo personalmente.",
    cta: { to: "/", label: "Abrir chat" },
  },
  {
    icon: Sparkles,
    color: "from-fuchsia-600 to-pink-600",
    title: "✨ Servicios y precios",
    desc: "En Servicios ves lecturas, trabajos de hechicería y pactos con su precio (sólo visible con tu cuenta). Toca Me interesa en el que necesitas y te muestro los métodos de pago.",
    cta: { to: "/servicios", label: "Ver servicios" },
  },
  {
    icon: CreditCard,
    color: "from-orange-500 to-red-600",
    title: "💵 Registrar tu pago en 3 pasos",
    desc: "Desde el chat o en Mi Cuenta → Mis Pagos: 1) elige el servicio, 2) indica si es abono o pago completo y el monto, 3) sube la foto de tu comprobante. Cristopher lo aprueba y te aviso por chat y por voz.",
    cta: { to: "/mi-cuenta?tab=pays", label: "Ver Mis Pagos" },
  },
  {
    icon: CalendarDays,
    color: "from-emerald-500 to-teal-600",
    title: "📅 Agenda tu cita desde el chat",
    desc: "En cuanto tu servicio quede liquidado aparece la agenda en el chat: verás entre dos y seis horarios por día en TU hora local y la hora de Cristopher. Elige uno, confirma y recibes tu confirmación al momento.",
  },
  {
    icon: User,
    color: "from-sky-500 to-cyan-600",
    title: "👤 Tu cuenta",
    desc: "En Mi Cuenta administras tu perfil y foto, lees tus mensajes y notificaciones, ves tus citas y tus pagos. Si necesitas corregir un dato, solicítalo y Cristopher lo revisa.",
    cta: { to: "/mi-cuenta", label: "Ir a Mi Cuenta" },
  },
  {
    icon: Sparkles,
    color: "from-violet-600 to-purple-700",
    title: "🔮 Tu lectura, paso a paso",
    desc: "Al contratar una lectura, Cristopher te enviará un enlace con la guía para tu lectura: ahí escribes tus datos y mínimo seis preguntas. Él prepara tu lectura y en la llamada recibes respuestas claras sobre tu pasado, presente y futuro.",
  },
  {
    icon: Mic,
    color: "from-indigo-600 to-blue-700",
    title: "🎙️ Vidente Virtual y Horóscopo",
    desc: "Con acceso habilitado puedes hablar en vivo con la Vidente Virtual por voz y recibir cada día tu horóscopo personalizado con lectura en voz alta. Se activan al liquidar un trabajo o cuando Cristopher te lo regala.",
  },
  {
    icon: Gift,
    color: "from-rose-600 to-red-700",
    title: "🎁 Sorteos, juegos y testimonios",
    desc: "Participa en los sorteos en vivo con la ruleta, juega los 13 mini-juegos espirituales mientras esperas tu lectura y lee los testimonios reales de otros clientes. Al terminar tu servicio, Cristopher te enviará un enlace para dejar el tuyo.",
    cta: { to: "/juegos", label: "Ir a Juegos" },
  },
  {
    icon: Bell,
    color: "from-yellow-500 to-amber-600",
    title: "🔔 Avisos en tiempo real y por voz",
    desc: "Te aviso al instante, y con voz si la tienes activada, cuando Cristopher apruebe o rechace un pago, confirme una cita, te habilite una lectura o inicie un sorteo. Activa las notificaciones cuando te lo pida para no perderte nada.",
  },
  {
    icon: Sparkle,
    color: "from-[#d4af37] to-amber-600",
    title: "¡Listo! Bendiciones para ti 🌟",
    desc: "Ya conoces lo esencial. Si tienes cualquier duda, abre el chat o toca el micrófono del asistente de voz y pregúntame. Te espero del otro lado.",
    final: true,
  },
];

export const Tutorial = ({ forceOpen, onClose }) => {
  const [step, setStep] = useState(0);
  const voice = useVoice();
  const [open, setOpen] = useState(() => {
    if (forceOpen) return true;
    try { return !localStorage.getItem(TUTORIAL_KEY); }
    catch { return false; }
  });

  const finish = (skipped = false) => {
    localStorage.setItem(TUTORIAL_KEY, skipped ? "skipped" : "completed");
    voice.stop();
    setOpen(false);
    if (onClose) onClose();
  };

  const s = STEPS[step];
  useEffect(() => {
    if (!open || !voice.isOn) return undefined;
    const t = setTimeout(() => voice.speak(`${s.title.replace(/[^\p{L}\p{N}\s¡!¿?,.:;]/gu, "").trim()}. ${s.desc}`, { id: `tutorial:${step}` }), 400);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step, voice.isOn]);

  if (!open) return null;

  const Icon = s.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <div
      data-testid="tutorial-overlay"
      className="fixed inset-0 z-[110] flex items-center justify-center px-4 py-8 bg-black/80 backdrop-blur-sm animate-in fade-in"
    >
      <div className="relative max-w-lg w-full glass-dark border border-[#d4af37]/30 rounded-3xl p-6 md:p-8 shadow-2xl">
        {/* Skip */}
        <button
          data-testid="tutorial-skip"
          onClick={() => finish(true)}
          className="absolute top-3 right-3 text-neutral-400 hover:text-white text-xs flex items-center gap-1"
          aria-label="Saltar tutorial"
        >
          <X className="w-4 h-4" /> Saltar
        </button>

        {/* Voz */}
        <div className="absolute top-3 left-3 flex items-center gap-1">
          <button type="button" data-testid="tutorial-voice-toggle" onClick={() => voice.setEnabled(!voice.isOn)} title={voice.isOn ? "Apagar voz" : "Leer con voz"}
            className={`w-7 h-7 rounded-full flex items-center justify-center ${voice.isOn ? "bg-[#d4af37] text-black" : "bg-white/10 text-neutral-400"}`}>
            {voice.isOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
          </button>
          {voice.speaking && <button type="button" data-testid="tutorial-voice-stop" onClick={voice.stop} className="h-7 px-2 rounded-full bg-red-500/20 text-red-200 text-[10px] flex items-center gap-1"><Square className="w-3 h-3" /> Detener</button>}
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-1 mb-5 mt-1">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1 rounded-full flex-1 transition-all ${i <= step ? "bg-[#d4af37]" : "bg-white/10"}`}
            />
          ))}
        </div>

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${s.color} flex items-center justify-center shadow-2xl shadow-purple-500/30`}>
            <Icon className="w-8 h-8 text-white" />
          </div>
        </div>

        {/* Content */}
        <h2 className="font-serif text-2xl md:text-3xl text-white text-center mb-3" data-testid="tutorial-title">
          {s.title}
        </h2>
        <p className="text-neutral-300 text-sm md:text-base text-center leading-relaxed mb-6" data-testid="tutorial-desc">
          {s.desc}
        </p>

        {s.cta && (
          <div className="flex justify-center mb-5">
            <Link
              to={s.cta.to}
              onClick={() => finish(false)}
              data-testid="tutorial-cta"
              className="text-xs uppercase tracking-widest border border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37]/10 rounded-full px-4 py-2"
            >
              {s.cta.label} →
            </Link>
          </div>
        )}

        {/* Nav */}
        <div className="flex items-center justify-between gap-3">
          <Button
            data-testid="tutorial-prev"
            onClick={() => setStep((x) => Math.max(0, x - 1))}
            disabled={step === 0}
            variant="ghost"
            className="text-neutral-400 hover:text-white disabled:opacity-30"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
          </Button>

          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">
            {step + 1} / {STEPS.length}
          </div>

          {!isLast ? (
            <Button
              data-testid="tutorial-next"
              onClick={() => setStep((x) => Math.min(STEPS.length - 1, x + 1))}
              className="btn-gold rounded-full uppercase text-xs tracking-widest px-4"
            >
              Siguiente <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              data-testid="tutorial-finish"
              onClick={() => finish(false)}
              className="btn-gold rounded-full uppercase text-xs tracking-widest px-4"
            >
              ¡Comenzar! <Sparkle className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>

        <p className="text-[10px] text-neutral-500 text-center mt-4">
          Puedes ver este tutorial de nuevo desde <span className="text-[#d4af37]">Mi Cuenta → Ayuda</span>.
        </p>
      </div>
    </div>
  );
};
